import { Theme } from '@mui/material'
import { IPosition, IPositionType } from 'src/generated-types'
import { IChangeType, IPositionChange, IProjectVersionChanges } from 'src/generated-types/project-version-changes'
import { MUIThemeColors } from 'src/mui-theme'
import { getThemeColor } from 'src/utils'

export const getChangeColor = (theme: Theme, type?: IChangeType) =>
  getThemeColor(
    theme,
    type === IChangeType.Added
      ? MUIThemeColors.successMain
      : type === IChangeType.Modified
      ? MUIThemeColors.warningMain
      : type === IChangeType.Moved
      ? MUIThemeColors.infoMain
      : type === IChangeType.Removed
      ? MUIThemeColors.errorMain
      : MUIThemeColors.secondaryLight,
  )

// A position counts once per kind it changed in, so a moved and modified position is in both.
export const countChanges = (changes?: IProjectVersionChanges) => {
  const positions = changes?.positions ?? []
  const count = (kind: IChangeType) => positions.filter(p => p.kinds.includes(kind)).length
  return {
    all: positions.length,
    added: count(IChangeType.Added),
    modified: count(IChangeType.Modified),
    moved: count(IChangeType.Moved),
    removed: count(IChangeType.Removed),
  }
}

// Changed positions of the chosen kind the position list leaves out because they have no drawing (a removed one by its
// published drawing).
export const countUnconfiguredChanges = (
  changes: IProjectVersionChanges,
  positions: IPosition[],
  basePositions: IPosition[],
  filter: RevisionFilter,
) => {
  const hasDrawing = (list: IPosition[]) => new Map(list.map(p => [p.name ?? '', p.positionDrawingId != null]))
  const draft = hasDrawing(positions)
  const base = hasDrawing(basePositions)
  return changes.positions.filter(
    change =>
      (filter === 'all' || change.kinds.includes(filter)) &&
      (change.type === IChangeType.Removed ? base.get(change.name) === false : draft.get(change.name) === false),
  ).length
}

const UNCONFIGURED_KEY: Record<RevisionFilter, string> = {
  all: 'revision-unconfigured-hidden',
  [IChangeType.Added]: 'revision-unconfigured-hidden-added',
  [IChangeType.Moved]: 'revision-unconfigured-hidden-moved',
  [IChangeType.Modified]: 'revision-unconfigured-hidden-modified',
  [IChangeType.Removed]: 'revision-unconfigured-hidden-removed',
}

export const unconfiguredNoteKey = (filter: RevisionFilter) => UNCONFIGURED_KEY[filter]

export const getRevisionBarColor = (theme: Theme, change?: IPositionChange) =>
  change ? getChangeColor(theme, change.type) : undefined

// The name a draft position has in the published version: a renamed position is the same position there.
export const publishedNameOf = (changeByName: Map<string, IPositionChange>, name?: string) =>
  changeByName.get(name ?? '')?.previousName ?? name ?? ''

// The changes of the draft's positions by their name, and of the positions it removed by their published name, apart:
// a removed position's name can be given to another position.
export const indexChanges = (changes?: IProjectVersionChanges) => {
  const positions = changes?.positions ?? []
  return {
    changeByName: new Map(positions.filter(p => p.type !== IChangeType.Removed).map(p => [p.name, p])),
    removedByName: new Map(positions.filter(p => p.type === IChangeType.Removed).map(p => [p.name, p])),
  }
}

// Changed positions per position type; removed ones count under the type they had in the published version.
export const countChangedByType = (
  {
    changeByName,
    removedByName,
    basePositions,
  }: {
    changeByName: Map<string, IPositionChange>
    removedByName: Map<string, IPositionChange>
    basePositions: IPosition[]
  },
  positions: IPosition[],
) => {
  const counts = new Map<IPositionType, number>()
  const count = (changed: Map<string, IPositionChange>, list: IPosition[]) => {
    const typeByName = new Map(list.map(p => [p.name ?? '', p.type]))
    changed.forEach((_, name) => {
      const type = typeByName.get(name)
      if (type != null) counts.set(type, (counts.get(type) ?? 0) + 1)
    })
  }
  count(changeByName, positions)
  count(removedByName, basePositions)
  return counts
}

export type RevisionFilter = 'all' | IChangeType

export const isChangeVisible = (change: IPositionChange | undefined, filter: RevisionFilter, onlyChanged: boolean) => {
  if (filter !== 'all') return !!change?.kinds.includes(filter)
  return !onlyChanged || !!change
}

export interface IChangeRow {
  type: IChangeType
  label: string
  before?: string
  after?: string
}

export type Translate = (key: string, options?: Record<string, string | number>) => string

const NONE = '—'
const productName = (number?: string, description?: string) => [number, description].filter(Boolean).join(' ')

// One line per concrete change (value, product or work item), for the tooltip and the list alike.
export const buildChangeRows = (change: IPositionChange, t: Translate): IChangeRow[] => {
  const value = (raw?: string) =>
    raw == null || raw === ''
      ? NONE
      : raw === 'true' || raw === 'false'
      ? t(raw === 'true' ? 'change-yes' : 'change-no')
      : raw

  const fieldRows = change.fields.map(field => ({
    type: IChangeType.Modified,
    label: t(`change-field-${field.field}`),
    before: value(field.before),
    after: value(field.after),
  }))

  const componentRows = change.components.flatMap((component): IChangeRow[] => {
    const name = productName(component.productNumber, component.description)
    if (component.type === IChangeType.Added) {
      return [{ type: IChangeType.Added, label: name, after: `×${component.quantityAfter}` }]
    }
    if (component.type === IChangeType.Removed) {
      return [{ type: IChangeType.Removed, label: name, before: `×${component.quantityBefore}` }]
    }
    const rows: IChangeRow[] = []
    if (component.previousProductNumber != null || component.previousDescription != null) {
      rows.push({
        type: IChangeType.Modified,
        label: t('change-replaced'),
        before: productName(component.previousProductNumber, component.previousDescription),
        after: name,
      })
    }
    if (component.quantityBefore !== component.quantityAfter) {
      rows.push({
        type: IChangeType.Modified,
        label: `${name} · ${t('change-quantity')}`,
        before: String(component.quantityBefore),
        after: String(component.quantityAfter),
      })
    }
    if (component.unitPriceBefore !== component.unitPriceAfter) {
      rows.push({
        type: IChangeType.Modified,
        label: `${name} · ${t('change-unit-price')}`,
        before: component.unitPriceBefore == null ? NONE : component.unitPriceBefore.toFixed(2),
        after: component.unitPriceAfter == null ? NONE : component.unitPriceAfter.toFixed(2),
      })
    }
    return rows
  })

  const workRows = change.work.map(work => ({
    type: work.type,
    label: `${t('change-field-work')}: ${productName(work.productNumber, work.description)}`,
    ...(work.type === IChangeType.Added ? { after: `×${work.quantity}` } : { before: `×${work.quantity}` }),
  }))

  return [...fieldRows, ...componentRows, ...workRows]
}

// Plain-text form for tooltips.
const changeRowText = (row: IChangeRow) => {
  if (row.before === undefined) return `+ ${row.label} ${row.after}`
  if (row.after === undefined) return `− ${row.label} ${row.before}`
  return `${row.label}: ${row.before} → ${row.after}`
}

const MAX_SUMMARY_ROWS = 6

// Tooltip body: the first few changes, then a count of the rest.
export const changeSummaryLines = (change: IPositionChange, t: Translate) => {
  const rows = buildChangeRows(change, t)
  const lines = rows.slice(0, MAX_SUMMARY_ROWS).map(changeRowText)
  return rows.length > MAX_SUMMARY_ROWS
    ? [...lines, t('change-more', { count: rows.length - MAX_SUMMARY_ROWS })]
    : lines
}

const COMPASS = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW']

// Translation key (projects namespace) of the compass direction for a bearing in degrees.
export const compassKey = (bearing: number) => `compass-${COMPASS[Math.round(bearing / 45) % 8]}`

const partName = (number?: string, description?: string) => description || number || ''

// Short sentences for a position's change chips, most telling first; identical ones are merged as "×n".
export const describeChange = (change: IPositionChange, t: Translate): string[] => {
  if (change.type === IChangeType.Added) return [t('change-describe-new')]
  if (change.type === IChangeType.Removed) return [t('change-describe-removed')]

  const sentences: string[] = []
  if (change.previousName) sentences.push(t('change-describe-renamed', { from: change.previousName }))
  const drawing = change.fields.find(f => f.field === 'drawing')
  if (drawing) {
    sentences.push(t('change-describe-drawing', { from: drawing.before ?? NONE, to: drawing.after ?? NONE }))
  }
  change.components.forEach(c => {
    const name = partName(c.productNumber, c.description)
    if (c.type === IChangeType.Added) sentences.push(t('change-describe-inserted', { name }))
    else if (c.type === IChangeType.Removed) sentences.push(t('change-describe-removed-part', { name }))
    else if (c.previousProductNumber != null || c.previousDescription != null) {
      sentences.push(`${partName(c.previousProductNumber, c.previousDescription)} → ${name}`)
    } else {
      sentences.push(t('change-describe-qty', { name, from: c.quantityBefore, to: c.quantityAfter }))
    }
  })
  change.work.forEach(w => {
    const name = partName(w.productNumber, w.description)
    sentences.push(
      t(w.type === IChangeType.Added ? 'change-describe-work-added' : 'change-describe-work-removed', { name }),
    )
  })
  const others = change.fields.filter(f => !['name', 'drawing', 'latitude', 'longitude'].includes(f.field))
  if (others.length > 0) {
    sentences.push(t('change-describe-fields', { fields: others.map(f => t(`change-field-${f.field}`)).join(', ') }))
  }
  if (change.move) {
    const direction = t(compassKey(change.move.bearing))
    sentences.push(t('change-describe-moved', { meters: Math.round(change.move.meters), direction }))
  }

  const merged = new Map<string, number>()
  sentences.forEach(s => merged.set(s, (merged.get(s) ?? 0) + 1))
  return [...merged].map(([text, n]) => (n > 1 ? `${text} ×${n}` : text))
}
